import React, { FC, useState } from "react";
import { Modal, Input, Upload, Button, Checkbox, Space, Row, Col } from "antd";
import {
  UploadOutlined,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { Table } from "antd";
import { Flex } from "antd/lib";
import { Typography } from "antd";
import { Tooltip } from "antd";

const { Text } = Typography;

interface DocumentTag {
  id: number;
  documentType: string;
  name: string;
  optional: boolean;
  procedureDocumentCode: string;
  enabled: boolean;
}

interface SelectedTag {
  id: number;
  name: string;
}

interface DocumentUploadModalProps {
  isVisible: boolean;
  onClose: () => void;
  documentType: any;
  setDocumentUrls: any;
  loading?: boolean;
  documentTags?: DocumentTag[];
  handleFileChange: any;
  documentUrls: {
    documentType: string;
    documentName: string;
    documentUrl: string;
    id: number;
    referenceType: string | null;
    documentLabelType: any;
    deficiencyCheckStatus: any;
    remarks: string | null;
    questions?: Array<{
      id: number;
      name: string;
    }>;
  }[];
  handleDeleteList: any;
  handleDownloadDocument: any;
  handleOpen: any;
  uploadFileList: any;
}

const DocumentUploadModal: FC<DocumentUploadModalProps> = ({
  isVisible,
  onClose,
  documentType,
  setDocumentUrls,
  loading = false,
  documentTags = [],
  handleFileChange,
  documentUrls,
  handleDeleteList,
  handleDownloadDocument,
  handleOpen,
  uploadFileList
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<SelectedTag[]>([]);
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);

  const handleTagChange = (id: number, tag: string) => {
    setSelectedTags((prev: SelectedTag[]) => {
      const exists = prev?.some((t: SelectedTag) => t.id === id);
      if (exists) {
        return prev?.filter((t: SelectedTag) => t.id !== id);
      }
      return [...prev, { id: id, name: tag }];
    });
  };

  const handleSave = async () => {
    const latestDocument = matchingDocuments?.[matchingDocuments.length - 1];
    
    if (latestDocument) {
      const updatedDocument = {
        ...latestDocument,
        questions: selectedTags
      };

      setDocumentUrls((prevDocuments: any[]) => {
        return prevDocuments?.map((doc) => {
          if (doc.id === latestDocument.id) {
            return updatedDocument;
          }
          return doc;
        });
      });

      setSelectedFile(null);
      setPreviewUrl("");
      setSelectedTags([]);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setSelectedTags([]);
    onClose();
  };

  const matchingDocuments = documentUrls?.filter(
    (doc) => doc.documentLabelType === documentType?.documentLabelType
  );

  // Get all selected questions from all documents
  const allSelectedQuestions = documentUrls?.reduce((acc: number[], doc) => {
    if (doc.questions) {
      const questionIds = doc.questions?.map((q: any) => q.id);
      return [...acc, ...questionIds];
    }
    return acc;
  }, []);

  // Check if a tag is already selected in any document
  const isTagSelected = (tagId: number) => {
    return allSelectedQuestions?.includes(tagId);
  };

  return (
    <>
      <Modal
        title="UPLOAD DOCUMENT"
        open={isVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            <div style={{ marginBottom: 8 }}>Document Type:</div>
            <Input value={documentType?.documentLabelType} disabled />
          </div>

          <div>
            {/* <div style={{ marginBottom: 8 }}>Select File:</div> */}
            {/* <Upload.Dragger
              beforeUpload={() => false}
              onChange={() => handleFileChange(documentType)}
              showUploadList={false}
              accept=".pdf,.jpg,.jpeg,.png"
              maxCount={1}
            >
              {!selectedFile ? (
                <>
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p>Drag & Drop File or Browse file</p>
                </>
              ) 
              : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <span>{selectedFile.name}</span>
                      {selectedFile.type.startsWith('image/') && (
                        <Button 
                          type="link" 
                          icon={<EyeOutlined />} 
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewVisible(true);
                          }}
                        >
                          Preview
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Upload.Dragger> */}
            {!documentType?.isMultiple && matchingDocuments?.length > 0 ? (
              <></>
            ) : (
              <Upload
                multiple={documentType?.isMultiple}
                 {...(documentType?.isMultiple ? { fileList: uploadFileList } : {})}
                showUploadList={false}
                beforeUpload={() => false}
                onChange={
                  handleFileChange &&
                  handleFileChange(documentType?.documentType, documentType?.isMultiple)
                }
                // disabled={!showData}
              >
                <Button
                  icon={<UploadOutlined />}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 0,
                    border: "none",
                    borderTopLeftRadius: "6px",
                    borderBottomLeftRadius: "6px",
                  }}
                  // disabled={!showData}
                >
                  {"Select File"}
                </Button>
              </Upload>
            )}

            {documentUrls && documentUrls?.length > 0 && (
              <Table
                showHeader={false}
                dataSource={documentUrls?.filter(
                  (doc) =>
                    doc?.documentLabelType === documentType?.documentLabelType
                )}
                columns={[
                  {
                    title: "Document Type",
                    dataIndex: "documentLabelType",
                    key: "documentType",
                    width: "30%",
                    render: (type: string) => (
                      <Flex justify="center">
                        <Text style={{ color: "#7c8181", fontSize: "14px" }}>
                          {type}
                        </Text>
                      </Flex>
                    ),
                  },
                  {
                    title: "Document Name",
                    dataIndex: "documentName",
                    key: "documentName",
                    width: "30%",

                    render: (name: string) => (
                      <Flex justify="center">
                        <Tooltip title={name}>
                          <Text
                            strong
                            style={{ color: "#7c8181", fontSize: "14px" }}
                          >
                            {name?.length > 10
                              ? name?.substring(0, 10) + "..."
                              : name}
                          </Text>
                        </Tooltip>
                      </Flex>
                    ),
                    // width: 520,
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: "40%",
                    render: (_, document, i) => (
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          justifyContent: "center",
                        }}
                      >
                        <DownloadOutlined
                          style={{
                            color: "#273881",
                            fontSize: "14px",
                          }}
                          onClick={() =>
                            handleDownloadDocument &&
                            handleDownloadDocument(
                              document?.documentName,
                              document?.documentUrl
                            )
                          }
                        />
                        <Tooltip title="View File">
                          <EyeOutlined
                            style={{
                              color: "#273881",
                              fontSize: "14px",
                            }}
                            onClick={() => {
                              handleOpen(document?.documentUrl);
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Delete File">
                          <DeleteOutlined
                            style={{
                              color: "#273881",
                              fontSize: "14px",
                              cursor: "pointer",
                            }}
                            // onClick={
                            //   !isReadOnly
                            //     ? () =>
                            //         handleDeleteList &&
                            //         handleDeleteList(
                            //           document?.documentLabelType,
                            //           document?.documentUrl,
                            //           i
                            //         )
                            //     : undefined
                            // }
                            onClick={() =>
                              handleDeleteList &&
                              handleDeleteList(
                                document?.documentLabelType,
                                document?.documentUrl,
                                i
                              )
                            }
                          />
                        </Tooltip>
                      </div>
                    ),
                  },
                ]}
                rowKey={(record) =>
                  record.documentName || `document-${record.documentUrl}`
                }
                pagination={false}
                className="showUploadDoc"
              />
            )}
          </div>

          <div>
            <div style={{ marginBottom: 8 }}>Documents Tags:</div>
            <Row gutter={[16, 16]}>
              {documentTags?.map((tag) => {
                const isAlreadySelected = isTagSelected(tag.id);
                const isCurrentlySelected = selectedTags?.some(t => t.id === tag.id);
                
                return (
                  <Col span={12} key={tag.name}>
                    <Checkbox
                      checked={selectedTags?.some((t: SelectedTag) => t.id === tag.id)}
                      onChange={() => handleTagChange(tag.id, tag.name)}
                      disabled={isAlreadySelected && !isCurrentlySelected}
                    >
                      <span style={{ 
                        color: isAlreadySelected && !isCurrentlySelected ? '#999' : 'inherit'
                      }}>
                        {tag.name}{" "}
                        {!tag.optional && <span style={{ color: "red" }}>*</span>}
                      </span>
                    </Checkbox>
                  </Col>
                );
              })}
            </Row>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 24,
            }}
          >
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={
                matchingDocuments?.length === 0 || selectedTags.length === 0
              }
              loading={loading}
            >
              SAVE
            </Button>
          </div>
        </Space>
      </Modal>

      {/* Preview Modal */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
      >
        {selectedFile?.type.startsWith("image/") && (
          <img alt="Preview" style={{ width: "100%" }} src={previewUrl} />
        )}
      </Modal>
    </>
  );
};

export default DocumentUploadModal;
